import { ApolloClient, InMemoryCache, createHttpLink, from, useApolloClient } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CognitoErrorCodes } from '@watter/shared-components';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import i18next from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { nanoid } from 'nanoid';
import { environment } from '../environments/environment';
import { revertAll } from '../store/actions';
import { store } from '../store/index';
import { hideLoader, setSession, showLoader, showNoInternetPopUp } from '../store/reducers/appSlice';
import { openToast } from '../store/reducers/toastSlice';
import { NavigationConfig } from '../utils/navigation-constants';
// GraphQL API endpoint
const httpLink = createHttpLink({
    uri: environment.API_END_POINT,
});

export const isTokenExpired = token => {
    try {
        const { exp } = jwtDecode(token);
        return Date.now() >= exp * 1000;
    } catch (err) {
        console.log('err ===>', err);
        return true; // treat invalid token as expired
    }
};
export const fetchCurrentSession = async () => {
    try {
        const storeToken = store?.getState()?.app?.session;
        console.log('isTokenExpired(storeToken?.token)==>', isTokenExpired(storeToken?.token));
        if (!isTokenExpired(storeToken?.token)) {
            return storeToken?.token;
        }
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.idToken?.toString();
        if (!token) {
            console.error('token is null, hence logging out user');
            store.dispatch(
                openToast({
                    show: true,
                    message: i18next.t('user_session_expired_error'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            setTimeout(() => {
                forceSignOutUser(); // added timeout to display toast message
            }, 3000);
        } else if (token && session?.idToken?.payload) {
            store.dispatch(
                setSession({
                    token: token,
                    payload: session?.idToken?.payload,
                }),
            );
        }
        return session?.tokens?.idToken?.toString();
    } catch (err) {
        console.error('err fetchAuthSession==>', err);
        if (err?.name === CognitoErrorCodes.USER_NOT_FOUND_EXCEPTION) {
            forceSignOutUser();
        }
    }
};

//  Authorization token to the headers
const authLink = setContext(async (_, { headers, fetchAWSToken = true }) => {
    let token;
    if (fetchAWSToken && environment.NODE_ENV !== 'test') {
        token = await fetchCurrentSession();
    }
    const traceId = nanoid();

    return {
        headers: {
            ...headers,
            authorization: `Bearer ${token}`,
            'x-trace-id': `WW-${traceId}`,
        },
    };
});

export const forceSignOutUser = () => {
    try {
        store.dispatch(showLoader());
        signOut()
            .then(res => {
                console.warn('res: ', res);
                store.dispatch(revertAll());
                localStorage.removeItem('authToken');
                sessionStorage.clear();
                useApolloClient().clearStore();
                window.location.href = NavigationConfig.Login;
            })
            .catch(err => {
                store.dispatch(hideLoader());
                console.error('err SignOut: ', err);
            })
            .finally(() => {
                store.dispatch(hideLoader());
                window.isShowingSessionExpired = false;
            });
    } catch (err) {
        store.dispatch(hideLoader());
        console.error('err forceSignOutUser==>', err);
    }
};
export const getErrorMessage = message => {
    const extractedError = message.replace(/.*Error: /, '');
    return extractedError;
};
// Error handling link
const handleInternalServerError = errorMessage => {
    switch (errorMessage) {
        case 'Token has expired':
            if (!window.isShowingSessionExpired) {
                window.isShowingSessionExpired = true;
                store.dispatch(
                    openToast({
                        show: true,
                        message: i18next.t('user_session_expired_error'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
                forceSignOutUser();
            }
            break;
        case 'User is inactive':
        case 'User is deleted':
            store.dispatch(
                openToast({
                    show: true,
                    message: i18next.t('inactive_account_error_message'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            forceSignOutUser();
            break;
        case 'Invalid Token':
            store.dispatch(
                openToast({
                    show: true,
                    message: i18next.t('invalid_token_error_message'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
            forceSignOutUser();
            break;
        default:
            store.dispatch(
                openToast({
                    show: true,
                    message: errorMessage,
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
    }
};
export const errorLink = onError(({ graphQLErrors, networkError }) => {
    console.log('graphQLErrors, networkError', graphQLErrors, networkError);
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, extensions }) => {
            switch (extensions?.code) {
                case 'BAD_USER_INPUT':
                    store.dispatch(
                        openToast({
                            show: true,
                            message: i18next.t('invalid_input_parameters'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                case '401':
                case 401:
                    store.dispatch(
                        openToast({
                            show: true,
                            message: getErrorMessage(message),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    forceSignOutUser();
                    break;
                case 'INTERNAL_SERVER_ERROR':
                    handleInternalServerError(getErrorMessage(message));
                    break;
                default:
                    console.error(message, 'error message');
            }
        });
    }

    if (networkError) {
        const statusCode = networkError?.statusCode;
        if (statusCode === 400) {
            // Bad Request: The request was malformed.
        } else if ('Failed to fetch' === networkError?.message) {
            store.dispatch(showNoInternetPopUp());
        }
    }
});

// Combine links (error handling, authentication, and HTTP)
const link = from([errorLink, authLink, httpLink]);

// Apollo Client instance
export const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
});
